import { Bell, CheckCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useNotifications } from "@/lib/notifications";

const AMBER = "245, 158, 11"; // fixed bright amber: the badge keeps dark text on every theme

function ago(iso) {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationBell({ testid = "notification-bell" }) {
  const n = useNotifications();
  if (!n) return null;
  return (
    <Popover>
      <PopoverTrigger className="relative inline-flex h-11 w-11 items-center justify-center rounded-full text-q-muted outline-none transition-colors hover:bg-q-raised hover:text-q-text" aria-label="Notifications" data-testid={testid}>
        <Bell className="h-5 w-5" />
        {n.unread > 0 && (
          <span className="absolute right-1 top-1 inline-flex min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-black leading-[18px]"
            style={{ background: `rgb(${AMBER})`, color: "#1a1204", boxShadow: `0 0 12px rgba(${AMBER},0.6)` }} data-testid={`${testid === "notification-bell" ? "notification" : "notification-mobile"}-unread-count`}>
            {n.unread > 99 ? "99+" : n.unread}
          </span>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(92vw,360px)] border-q-line bg-q-surface p-0" data-testid="notification-center">
        <div className="flex items-center justify-between border-b border-q-border px-4 py-3">
          <p className="font-display text-sm font-extrabold text-q-text">Notifications</p>
          <button className="q-btn q-btn-ghost min-h-[36px] px-2 py-1 text-xs" disabled={!n.unread} onClick={n.markAll} data-testid="notification-mark-all-read">
            <CheckCheck className="h-3.5 w-3.5" /> Mark all read
          </button>
        </div>
        <ul className="max-h-[60vh] overflow-y-auto" data-testid="notification-list">
          {n.notifications.length === 0 && <li className="px-4 py-8 text-center text-sm text-q-muted" data-testid="notification-empty">You're all caught up.</li>}
          {n.notifications.map((x) => (
            <li key={x.id}>
              <button onClick={() => n.open(x)} className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-q-raised"
                style={x.read ? undefined : { background: `rgba(${AMBER},0.08)` }} data-testid={`notification-item-${x.id}`} data-unread={x.read ? "0" : "1"}>
                <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full" style={{ background: x.read ? "transparent" : `rgb(${AMBER})` }} />
                <span className="min-w-0 flex-1">
                  <span className={`block text-sm ${x.read ? "text-q-muted" : "font-semibold text-q-text"}`} data-testid="notification-title">{x.title}</span>
                  <span className="mt-0.5 block text-[11px] text-q-faint">{ago(x.created_at)}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
